import {
    Box,
    Button,
    Container, // Added Container for better spacing
    Typography,
    Paper,
    CircularProgress, // Added for loading state
    Alert, // Added for error state
    Divider, // Added for visual separation
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material'; // Added back icon
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

import BannerSection from "../components/BannerSection";
import Footer from '../components/Footer';
import axiosInstance from '../services/axiosInstance';

// ==============================
// TYPE DEFINITIONS
// ==============================

interface ArticleDetailData {
    articleId: number;
    contentDetails: string; // The HTML content from API
}

const ArticlePage = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>(); // 1. Get ID from URL

    // State for Layout (Banner)
    const [loadingBanner, setLoadingBanner] = useState(false);
    const [adSpaceBanner, setAdSpaceBanner] = useState<any[]>([]);

    // State for Single Article
    const [article, setArticle] = useState<ArticleDetailData | null>(null);
    const [loadingArticle, setLoadingArticle] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // 1. Fetch Banner Data (Existing Logic)
    useEffect(() => {
        const fetchBannerData = async () => {
            setLoadingBanner(true);
            try {
                const response = await axiosInstance.post(
                    "/CustomerVehicle/VehicleMultipleDataList",
                    { adSpaceData: true },
                    { headers: { "Content-Type": "application/json" } }
                );

                const result = response.data as {
                    responseCode: string;
                    responseMessage?: string;
                    responseData?: {
                        adSpaceData?: any[];
                        articleData?: any[];
                    };
                };
                if (result.responseCode === "00" && result.responseData?.adSpaceData) {
                    const homePageBanner = result.responseData.adSpaceData.filter(
                        (ad: any) => ad.adPositionCode === "homePage-Banner"
                    );
                    setAdSpaceBanner(homePageBanner);
                }
            } catch {
                console.log("❌ Error fetching ad space data");
            } finally {
                setLoadingBanner(false);
            }
        };

        fetchBannerData();
    }, []);

    // 2. Fetch Single Article Data (New Logic)
    useEffect(() => {
        const fetchArticle = async () => {
            if (!id) return; // If no ID, don't fetch (or handle as "list view" if you prefer)

            setLoadingArticle(true);
            setError(null);
            try {
                const response = await axiosInstance.get(
                    `/CustomerVehicle/ArticleSingleData/${id}`
                );

                const result = response.data as {
                    responseCode: string;
                    responseMessage?: string;
                    responseData?: {
                        articleId: number;
                        contentDetails: string;
                    };
                };

                if (result.responseCode === "00" && result.responseData) {
                    setArticle(result.responseData);
                } else {
                    setError(result.responseMessage || "Failed to load article.");
                }
            } catch (err) {
                console.error("API Error:", err);
                setError("An error occurred while fetching the article.");
            } finally {
                setLoadingArticle(false);
            }
        };

        fetchArticle();
    }, [id]);

    return (
        <>
            {/* Banner */}
            <BannerSection adSpaceBanner={adSpaceBanner} />

            {/* Main Content Area */}
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 1200,
                    mx: 'auto',
                    mt: -4, // Overlap effect (if desired) or remove/adjust
                    position: 'relative',
                    zIndex: 10,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start', // Align to top, not center
                    minHeight: '60vh',
                    pb: 8
                }}
            >
                <Container maxWidth="md">
                    {/* Back Button */}
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start' }}>
                        <Button
                            startIcon={<ArrowBack />}
                            onClick={() => navigate(-1)}
                            variant="contained"
                            sx={{ bgcolor: 'white', color: 'black', '&:hover': { bgcolor: '#f5f5f5' } }}
                        >
                            Back
                        </Button>
                    </Box>

                    {/* Loading State */}
                    {loadingArticle && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {/* Error State */}
                    {!loadingArticle && error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {/* Article Content */}
                    {!loadingArticle && article && (
                        <Paper
                            elevation={3}
                            sx={{
                                p: { xs: 3, md: 5 },
                                borderRadius: 4,
                                bgcolor: 'background.paper',
                                width: '100%'
                            }}
                        >
                            {/* Render HTML Content safely */}
                            <Box
                                sx={{
                                    // Styles for the injected HTML tags
                                    '& h3': {
                                        fontSize: { xs: '1.5rem', md: '2rem' },
                                        fontWeight: 800,
                                        mb: 2,
                                        color: 'text.primary',
                                        lineHeight: 1.3
                                    },
                                    '& p': {
                                        fontSize: '1.1rem',
                                        lineHeight: 1.8,
                                        mb: 2.5,
                                        color: 'text.secondary',
                                    },
                                    '& strong': {
                                        fontWeight: 700,
                                        color: '#333'
                                    },
                                    '& img': {
                                        maxWidth: '100%',
                                        height: 'auto',
                                        borderRadius: '8px',
                                        my: 2
                                    }
                                }}
                                dangerouslySetInnerHTML={{ __html: article.contentDetails }}
                            />

                            {/* <Divider sx={{ my: 4 }} />

                            <Typography variant="caption" color="text.secondary">
                                Article ID: {article.articleId}
                            </Typography> */}
                        </Paper>
                    )}
                </Container>
            </Box>

            <Footer />
        </>
    );
};

export default ArticlePage;